import React, { FC, useEffect, useMemo, useState } from 'react';
import {
	Form,
	InputNumber,
	message,
	Popover,
	Radio,
	Select,
	Slider,
	SliderSingleProps,
} from 'antd';
import { connect } from 'react-redux';
import BudgetForEachCycleFormField from './BudgetForEachCycleFormField';
import BuyerRegionsLocationsTreeSelect from '../regions_tree_select/RegionsLocationsTreeSelect';
import { fetchAllLocationsForBuyer } from '../../thunks/locations_thunks';
import DistinctLocationBudgetComponent from './DistinctLocationBudgetComponent';
import { InfoCircleOutlined } from '@ant-design/icons';
import { nullSafeGet } from '../../utils/DataAccessUtils';

const BUDGETS_FORM_LOCATIONS_AUTOCOMPLETE = 'BUDGETS_FORM_LOCATIONS_AUTOCOMPLETE';

export const BUDGET_TYPE = {
	shared: 'shared',
	distinct: 'distinct',
	sharedForEachPeriod: 'sharedForEachPeriod',
};

const BudgetThirdSteps: FC<any> = ({
	step,
	form,
	formData,
	currency,
	locationDistinctBudget,
	setLocationDistinctBudget,

	locations,
	fetchAllLocations,
}): React.ReactElement => {
	const [fetchingAllLocations, setFetchingAllLocations] = useState(false);
	const [allLocations, setAllLocations] = useState([]);

	const hidden = useMemo(() => step !== 2, [step]);

	const { setFieldsValue } = form;

	const values: any = Form.useWatch([], form);
	const months = Form.useWatch('months', form);
	const budgetShareType = Form.useWatch('budgetShareType', form);
	const companyWideBudgetShareType = Form.useWatch('companyWideBudgetShareType', form);
	const sharedBudget = Form.useWatch('sharedBudget', form);
	const locationIds = Form.useWatch('locationIds', form);
	const isCompanyWideBudget = Form.useWatch('isCompanyWide', form);

	const isBudgetDistinctForEachLoc = useMemo(
		() => budgetShareType === BUDGET_TYPE.distinct,
		[budgetShareType]
	);

	const showVariablePeriod = useMemo(() => months === -1, [months]);

	const isBudgetSharedForEachPeriod = useMemo(
		() =>
			showVariablePeriod &&
			(budgetShareType === BUDGET_TYPE.sharedForEachPeriod ||
				companyWideBudgetShareType === BUDGET_TYPE.sharedForEachPeriod),
		[budgetShareType, companyWideBudgetShareType, showVariablePeriod]
	);

	useEffect(() => {
		if (isBudgetDistinctForEachLoc && !fetchingAllLocations && !nullSafeGet('0', allLocations)) {
			setFetchingAllLocations(true);
			fetchAllLocations()
				.then((res) => {
					setAllLocations(res);
				})
				.catch(() => message.error('Unable to fetch all locations'))
				.finally(() => setFetchingAllLocations(false));
		}
	}, [allLocations, fetchAllLocations, fetchingAllLocations, isBudgetDistinctForEachLoc]);

	useEffect(() => {
		if (!!nullSafeGet('0', allLocations)) {
			const locations = locationIds
				? allLocations.filter((loc) => locationIds.includes(loc.id))
				: allLocations;
			const { budgetPerLocations = [] } = formData;
			setLocationDistinctBudget(
				locations.map((loc) => {
					const locationBudget =
						budgetPerLocations.find((budgetloc) => budgetloc.locationId === loc.id) || {};
					return {
						...loc,
						...locationBudget,
					};
				})
			);
		}
	}, [allLocations, sharedBudget, locationIds]);

	const showSharedBudgetField = useMemo(
		() => !isBudgetSharedForEachPeriod && !isBudgetDistinctForEachLoc,
		[isBudgetDistinctForEachLoc, isBudgetSharedForEachPeriod]
	);

	const marks: SliderSingleProps['marks'] = {
		0: '0%',
		100: {
			style: { color: 'black' },
			label: <strong>100%</strong>,
		},
	};

	return (
		<>
			<Form.Item
				hidden={hidden}
				name="isCompanyWide"
				label={
					<div>
						<span>Budget Basis</span>
					</div>
				}
				initialValue={true}
			>
				<Radio.Group>
					<Radio.Button style={{ flexWrap: 'nowrap' }} value={true}>
						Company Wide
					</Radio.Button>
					<Radio.Button style={{ flexWrap: 'nowrap' }} value={false}>
						Location Based
					</Radio.Button>
				</Radio.Group>
			</Form.Item>
			{isCompanyWideBudget ? (
				showVariablePeriod ? (
					<Form.Item
						label={'Budget Type'}
						initialValue={
							formData.sharedBudget ? BUDGET_TYPE.shared : BUDGET_TYPE.sharedForEachPeriod
						}
						name="companyWideBudgetShareType"
						hidden={hidden}
					>
						<Select>
							<Select.Option value={BUDGET_TYPE.shared}>Same budget for each period.</Select.Option>
							<Select.Option value={BUDGET_TYPE.sharedForEachPeriod}>
								Budget specified for each period.
							</Select.Option>
						</Select>
					</Form.Item>
				) : null
			) : (
				<Form.Item
					label={'Budget Type'}
					initialValue={
						formData.sharedBudget
							? BUDGET_TYPE.shared
							: formData.budgetPerPeriod
							? BUDGET_TYPE.sharedForEachPeriod
							: BUDGET_TYPE.distinct
					}
					name="budgetShareType"
					hidden={hidden}
				>
					<Select>
						<Select.Option value={BUDGET_TYPE.shared}>
							Budget will be shared amongst the selected locations.
						</Select.Option>
						<Select.Option value={BUDGET_TYPE.distinct}>
							Budget will be distinct for each location.
						</Select.Option>
						{showVariablePeriod ? (
							<Select.Option value={BUDGET_TYPE.sharedForEachPeriod}>
								Budget will be shared amongst the selected locations, specified for each period.
							</Select.Option>
						) : null}
					</Select>
				</Form.Item>
			)}
			{isBudgetSharedForEachPeriod ? (
				<div style={{ display: hidden ? 'none' : 'block' }}>
					<BudgetForEachCycleFormField
						initialValue={formData.budgetPerPeriod}
						form={form}
						currency={currency}
					/>
				</div>
			) : null}
			{values && !values.isCompanyWide ? (
				<Form.Item hidden={hidden} name="locationIds" label="Locations">
					<BuyerRegionsLocationsTreeSelect
						mode="multiple"
						emptyWhenDisabled={false}
						filtersSlice={locations}
						filtersValueAccessor={() => form.getFieldValue('locationIds')}
						targetCollectionName={BUDGETS_FORM_LOCATIONS_AUTOCOMPLETE}
						renderRecord={(d) => (
							<Select.Option value={d.id} key={d.id}>
								<div>{d.name}</div>
							</Select.Option>
						)}
						sortBy={{ sort_by: 'name', order: 'ascend' }}
					/>
				</Form.Item>
			) : null}
			{showSharedBudgetField ? (
				<Form.Item
					hidden={hidden || isBudgetDistinctForEachLoc}
					name="sharedBudget"
					rules={[{ required: true, message: 'Please input a budget amount.' }]}
					label={
						<div>
							<span>Shared Budget</span>
							<Popover
								content={
									<span>
										Budget amount is shared{' '}
										{values && values.isCompanyWide ? 'company wide' : 'by each location'}{' '}
										{values && values.recurring ? 'and resets each period' : ''}.
									</span>
								}
							>
								<InfoCircleOutlined style={{ marginLeft: '12px' }} />
							</Popover>
						</div>
					}
					required={true}
				>
					<InputNumber addonAfter={currency.id} min={0} />
				</Form.Item>
			) : null}
			{values && !values.isCompanyWide && isBudgetDistinctForEachLoc ? (
				<DistinctLocationBudgetComponent
					locationDistinctBudget={locationDistinctBudget}
					loading={fetchingAllLocations}
					onEditBudget={setLocationDistinctBudget}
					allowedBudget={sharedBudget}
					hidden={hidden}
				/>
			) : null}
			<Form.Item
				hidden={hidden}
				name="thresholdPercentage"
				label="Alert Threshold"
				initialValue={90}
			>
				<Slider marks={marks} />
			</Form.Item>
		</>
	);
};

const mapStateToProps = (state) => ({
	locations: state.locations,
});

const mapDispatchToProps = (dispatch) => ({
	fetchAllLocations: (params) => dispatch(fetchAllLocationsForBuyer(params)),
});

export default connect(mapStateToProps, mapDispatchToProps)(BudgetThirdSteps as any);
